import { StockItem, StockPackage } from '@/types/stock';
import { StockCategory } from '@/types/stockCategory';
import { formatLocalDate, parseLocalDate } from '@/lib/dateUtils';
import { isAuthRequired } from '@/lib/environment';

const STOCK_ITEMS_CACHE_PREFIX = 'clando-stock-items-cache:';
const STOCK_PACKAGES_CACHE_PREFIX = 'clando-stock-packages-cache:';
const STOCK_CATEGORIES_CACHE_PREFIX = 'clando-stock-categories-cache:';
const LAST_USER_ID_KEY = 'team-v-does-last-user-id';

type CachePayload<T> = {
  v: 1;
  teamId: string;
  userId: string | null;
  savedAt: string;
  data: T[];
};

// --- Item Serialization ---

export const serializeStockItem = (item: StockItem) => ({
  ...item,
  createdAt: item.createdAt.toISOString(),
  updatedAt: item.updatedAt.toISOString(),
  expiryDate: item.expiryDate ? formatLocalDate(item.expiryDate) : null,
});

export const deserializeStockItem = (raw: any): StockItem => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
  updatedAt: new Date(raw.updatedAt),
  expiryDate: raw.expiryDate ? parseLocalDate(raw.expiryDate) : undefined,
});

// --- Package Serialization ---

export const serializeStockPackage = (pkg: StockPackage) => ({
  ...pkg,
  createdAt: pkg.createdAt.toISOString(),
  updatedAt: pkg.updatedAt.toISOString(),
  purchaseDate: pkg.purchaseDate ? formatLocalDate(pkg.purchaseDate) : null,
  expiryDate: pkg.expiryDate ? formatLocalDate(pkg.expiryDate) : null,
  expectedDeliveryDate: pkg.expectedDeliveryDate ? formatLocalDate(pkg.expectedDeliveryDate) : null,
});

export const deserializeStockPackage = (raw: any): StockPackage => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
  updatedAt: new Date(raw.updatedAt),
  purchaseDate: raw.purchaseDate ? parseLocalDate(raw.purchaseDate) : undefined,
  expiryDate: raw.expiryDate ? parseLocalDate(raw.expiryDate) : undefined,
  expectedDeliveryDate: raw.expectedDeliveryDate ? parseLocalDate(raw.expectedDeliveryDate) : undefined,
});

// --- Category Serialization ---

export const serializeStockCategory = (cat: StockCategory) => ({
  ...cat,
  createdAt: cat.createdAt.toISOString(),
});

export const deserializeStockCategory = (raw: any): StockCategory => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
});

// --- Generic Cache Helpers ---

const getCacheKey = (prefix: string, teamId: string) => `${prefix}${teamId}`;

const loadFromCache = <T>(prefix: string, teamId: string, deserializer: (raw: any) => T): T[] => {
  if (isAuthRequired()) {
    try {
      if (!localStorage.getItem(LAST_USER_ID_KEY)) return [];
    } catch { return []; }
  }

  try {
    const raw = localStorage.getItem(getCacheKey(prefix, teamId));
    if (!raw) return [];

    const parsed: CachePayload<T> = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || parsed.teamId !== teamId || !Array.isArray(parsed.data)) return [];

    const currentUserId = (() => {
      try { return localStorage.getItem(LAST_USER_ID_KEY); } catch { return null; }
    })();

    if (parsed.userId && currentUserId && parsed.userId !== currentUserId && isAuthRequired()) {
      return [];
    }

    return parsed.data.map(deserializer);
  } catch {
    return [];
  }
};

const saveToCache = <T>(prefix: string, teamId: string, data: T[], serializer: (item: T) => any) => {
  try {
    const userId = (() => {
      try { return localStorage.getItem(LAST_USER_ID_KEY); } catch { return null; }
    })();

    const payload: CachePayload<any> = {
      v: 1,
      teamId,
      userId,
      savedAt: new Date().toISOString(),
      data: data.map(serializer),
    };

    localStorage.setItem(getCacheKey(prefix, teamId), JSON.stringify(payload));
  } catch {
    // ignore
  }
};

// --- Exported Specific Helpers ---

export const loadStockItemsFromCache = (teamId: string) => loadFromCache(STOCK_ITEMS_CACHE_PREFIX, teamId, deserializeStockItem);
export const saveStockItemsToCache = (teamId: string, items: StockItem[]) => saveToCache(STOCK_ITEMS_CACHE_PREFIX, teamId, items, serializeStockItem);

export const loadStockPackagesFromCache = (teamId: string) => loadFromCache(STOCK_PACKAGES_CACHE_PREFIX, teamId, deserializeStockPackage);
export const saveStockPackagesToCache = (teamId: string, pkgs: StockPackage[]) => saveToCache(STOCK_PACKAGES_CACHE_PREFIX, teamId, pkgs, serializeStockPackage);

export const loadStockCategoriesFromCache = (teamId: string) => loadFromCache(STOCK_CATEGORIES_CACHE_PREFIX, teamId, deserializeStockCategory);
export const saveStockCategoriesToCache = (teamId: string, cats: StockCategory[]) => saveToCache(STOCK_CATEGORIES_CACHE_PREFIX, teamId, cats, serializeStockCategory);
